import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Modal, Title } from '@common/components';
import { Colors, Spacing } from '@common/tokens';
import { LoadingGrid } from './_loading-grid';
import { LoadingStep } from './_loading-step';
import type { StepStatusType } from './_step-indicator';

/** How long each step holds before the next is highlighted, in ms. */
const STEP_INTERVAL_MS = 20_000;
/**
 * How long the fully-completed state (100% grid, every step checked green)
 * lingers before `onComplete` fires, in ms.
 */
const COMPLETION_HOLD_MS = 2_000;

export type LoadingGridStep = {
  /** Stable identity for React reconciliation — not shown to the user. */
  key: string;
  /** The text rendered for the step. */
  label: string;
};

/**
 * Full-screen loading modal for long-running, multi-step work. Checkmark on
 * done, pulsing dot + progress bar on current, muted dot on pending. When the
 * caller flips `open` to false, it snaps to the fully-finished state (100%
 * grid, every step checked green), holds it for two seconds, then hides and
 * calls `onComplete`.
 *
 * Two modes:
 * - **Timed** (default, `currentStep` omitted): steps auto-advance on a timer
 *   every `stepIntervalMs`, with the last step held active until close.
 * - **Controlled** (`currentStep` provided): the active step mirrors the
 *   caller's value, the internal timer is disabled, and the active step's bar
 *   decelerates (never completing) until the caller advances `currentStep`.
 */
export function FullScreenLoadingGrid({
  open,
  title,
  steps,
  currentStep,
  stepIntervalMs = STEP_INTERVAL_MS,
  onComplete,
  className,
}: {
  /** Controls visibility. The step timer runs only while open. */
  open: boolean;
  /** Heading shown above the grid. */
  title: string;
  /** Narrated steps, in order. Each needs a stable `key` and a `label`. */
  steps: LoadingGridStep[];
  /**
   * Controlled active-step index. When provided, the component renders directly
   * from it (steps before it are done, this one is current, later ones pending)
   * and the internal timer is disabled. Omit it to keep the timed behavior.
   */
  currentStep?: number;
  /** How long each step holds, in ms. Defaults to 20000. Ignored in controlled mode. */
  stepIntervalMs?: number;
  /** Fired once the modal has finished its closing animation and hidden. */
  onComplete?: () => void;
  className?: string;
}) {
  const isControlled = currentStep !== undefined;

  // Own visibility so the modal can linger on the completed state after the
  // caller has already flipped `open` to false.
  const [visible, setVisible] = useState(open);
  // The active step actually rendered: timer-driven in timed mode, mirrored
  // from `currentStep` in controlled mode, snapped to all-done on close.
  const [displayStep, setDisplayStep] = useState(currentStep ?? 0);
  // Bumped on each advance so the current step's progress bar restarts its animation.
  const [progressNonce, setProgressNonce] = useState(0);

  // Hold the latest onComplete without re-arming the timers when the prop changes.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Track committed visibility so the close effect can tell a real open→false
  // transition (animate the finish) from a modal that was never opened.
  const visibleRef = useRef(visible);
  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  // Controlled mode: mirror the caller's `currentStep` and restart the active
  // step's progress animation on each advance. The internal timer stays off.
  useEffect(() => {
    if (!isControlled || !open) return;

    setVisible(true);
    setDisplayStep(currentStep);
    setProgressNonce((n) => n + 1);
  }, [isControlled, open, currentStep]);

  // Timed mode: narrate the steps on a timer. The last step stays active
  // (pulsing) until the caller closes the modal — completion is driven by
  // `open`, not the clock.
  useEffect(() => {
    if (isControlled || !open) return undefined;

    setVisible(true);
    setDisplayStep(0);
    setProgressNonce(0);

    const interval = setInterval(() => {
      setDisplayStep((prev) => {
        if (prev >= steps.length - 1) return prev;
        setProgressNonce((n) => n + 1);
        return prev + 1;
      });
    }, stepIntervalMs);

    return () => clearInterval(interval);
  }, [isControlled, open, steps.length, stepIntervalMs]);

  // When the caller closes an open modal, snap to the fully-completed state
  // (100% grid, every step checked green), hold it, then hide and notify.
  // Applies to both modes.
  useEffect(() => {
    if (open || !visibleRef.current) return undefined;

    setDisplayStep(steps.length);

    const hold = setTimeout(() => {
      setVisible(false);
      onCompleteRef.current?.();
    }, COMPLETION_HOLD_MS);

    return () => clearTimeout(hold);
  }, [open, steps.length]);

  return (
    <Modal
      visible={visible}
      width="100vw"
      footer={<></>}
      preventClose
      maskClosable={false}
      destroyOnClose
      className={clsx('wk-full-screen-loading-grid', className)}
    >
      <div className="wk-full-screen-loading-grid__content">
        <Title size="h3" weight="bold">
          {title}
        </Title>

        <LoadingGrid currentStep={displayStep} totalSteps={steps.length} />

        <div className="wk-full-screen-loading-grid__steps">
          {steps.map((step, i) => (
            <LoadingStep
              key={step.key}
              label={step.label}
              status={stepStatus(i, displayStep)}
              progressKey={progressNonce}
              durationMs={stepIntervalMs}
              // In controlled mode every active step has an unknown duration, so
              // it stalls; in timed mode only the held final step does.
              stalls={isControlled || i === steps.length - 1}
            />
          ))}
        </div>
      </div>

      <style jsx>{`
        /*
         * Modal chrome overrides must be :global — they target the AntModal
         * root, which styled-jsx can't scope. Everything else lives, scoped,
         * inside the private subcomponent that renders it.
         */
        :global(.wk-full-screen-loading-grid) {
          max-width: 100vw;
          top: 0;
          margin: 0;
          padding: 0;
        }
        :global(.wk-full-screen-loading-grid .ant-modal-content) {
          height: 100vh;
          border-radius: 0;
          box-shadow: none;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${Colors.steelLightest};
        }

        .wk-full-screen-loading-grid__content {
          display: flex;
          flex-direction: column;
          gap: ${Spacing.xl}px;
          width: 300px;
        }
        .wk-full-screen-loading-grid__steps {
          display: flex;
          flex-direction: column;
          gap: ${Spacing.md}px;
        }
      `}</style>
    </Modal>
  );
}

/** Where `index` sits relative to the currently active step. */
function stepStatus(index: number, currentStep: number): StepStatusType {
  if (index < currentStep) return 'done';
  if (index === currentStep) return 'current';
  return 'pending';
}
